/**
 * DeliverabilityHealthPage — Measure pillar analytics surface.
 *
 * Per audit 06G_DELIVERABILITY_HEALTH.md + Principle 3.11.8 (HARD pillar split).
 *
 * ONE read-only page that fetches the live deliverability snapshot and renders
 * the Transactional (slate) and Marketing (gold) pillars in two side-by-side
 * panels. The two pillars are intentionally NEVER blended into a single chart —
 * different KPIs, different thresholds, different sender tracks.
 *
 * Data source:
 *   GET /api/admin/email-analytics/deliverability?period=<today|Nd|custom&from&to>
 *     → data.{ transactional, marketing }
 *     each: { sent, delivered, delivery_rate, bounce_rate, hard_bounce_rate,
 *             open_rate, click_rate, unsub_rate }
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import PillarPageShell from "../shared/PillarPageShell";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsQuery } from "../shared/emailAnalyticsRange";
import { describeRange } from "../../common/timeRange";

import apiService from "../../../services/api";

// ─────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────
interface PillarDeliverability {
  sent: number | null;
  delivered: number | null;
  delivery_rate: number | null;
  bounce_rate: number | null;
  hard_bounce_rate: number | null;
  open_rate: number | null;
  click_rate: number | null;
  unsub_rate: number | null;
}

interface DeliverabilityResponse {
  transactional?: PillarDeliverability | null;
  marketing?: PillarDeliverability | null;
}

// ─────────────────────────────────────────────────────────────────────────
// Formatting helpers — null-safe, never crash
// ─────────────────────────────────────────────────────────────────────────
const EMPTY = "—";

const fmtCount = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n) ? n.toLocaleString() : EMPTY;

const fmtPct = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n) ? `${n.toFixed(2)}%` : EMPTY;

/**
 * Health color for a rate. `goodHigh` = bigger is better (delivery / open /
 * click). When false, smaller is better (bounce / hard-bounce / unsub).
 */
const rateColor = (
  value: number | null | undefined,
  good: number,
  warn: number,
  goodHigh: boolean,
): string => {
  if (value == null || !Number.isFinite(value)) return "#5D6D7E"; // neutral slate for no-data
  if (goodHigh) {
    if (value >= good) return "#146152"; // teal — healthy
    if (value >= warn) return "#D4AC0D"; // gold — watch
    return "#7D1935"; // burgundy — bad
  }
  // smaller is better
  if (value <= good) return "#146152"; // teal — healthy
  if (value <= warn) return "#D4AC0D"; // gold — watch
  return "#7D1935"; // burgundy — bad
};

// ─────────────────────────────────────────────────────────────────────────
// Single metric tile
// ─────────────────────────────────────────────────────────────────────────
interface MetricTileProps {
  label: string;
  value: string;
  color?: string;
  caption?: string;
}

const MetricTile: React.FC<MetricTileProps> = ({ label, value, color, caption }) => (
  <Card
    sx={{
      borderRadius: 2,
      height: "100%",
      boxShadow: "0 2px 5px rgba(0,0,0,0.04)",
    }}
  >
    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
      <Typography
        sx={{
          color: "text.secondary",
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "0.72rem",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontWeight: 700,
          fontSize: "1.6rem",
          color: color ?? "#1A5F7A",
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────
// One pillar panel (slate = transactional, gold = marketing)
// ─────────────────────────────────────────────────────────────────────────
interface PillarPanelProps {
  accent: string;
  badge: string;
  badgeIcon: string;
  title: string;
  subtitle: string;
  data: PillarDeliverability | null | undefined;
}

const PillarPanel: React.FC<PillarPanelProps> = ({
  accent,
  badge,
  badgeIcon,
  title,
  subtitle,
  data,
}) => {
  const deliveryRate = data?.delivery_rate;
  const sent = data?.sent;
  const delivered = data?.delivered;

  return (
    <Card
      sx={{
        borderRadius: 2,
        borderTop: `6px solid ${accent}`,
        height: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <CardContent>
        {/* Panel header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                fontSize: "1.35rem",
                color: "#1A5F7A",
                lineHeight: 1.1,
              }}
            >
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {subtitle}
            </Typography>
          </Box>
          <Chip
            label={`${badgeIcon} ${badge}`}
            size="small"
            sx={{
              bgcolor: accent,
              color: accent === "#D4AC0D" ? "#1B1F23" : "#FFFFFF",
              fontWeight: 700,
              fontFamily: '"Montserrat", sans-serif',
              fontSize: "0.68rem",
              letterSpacing: "0.5px",
            }}
          />
        </Box>

        {/* Sent / Delivered headline strip */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            mt: 1.5,
            mb: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: "rgba(0,0,0,0.025)",
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Sent
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1A5F7A" }}>
              {fmtCount(sent)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Delivered
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#1A5F7A" }}>
              {fmtCount(delivered)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Delivery rate
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: rateColor(deliveryRate, 98, 95, true),
              }}
            >
              {fmtPct(deliveryRate)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Metric cards grid */}
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <MetricTile
              label="Delivery Rate"
              value={fmtPct(data?.delivery_rate)}
              color={rateColor(data?.delivery_rate, 98, 95, true)}
              caption="Target ≥ 98%"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricTile
              label="Bounce Rate"
              value={fmtPct(data?.bounce_rate)}
              color={rateColor(data?.bounce_rate, 1, 2, false)}
              caption="Target ≤ 1%"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricTile
              label="Hard Bounce Rate"
              value={fmtPct(data?.hard_bounce_rate)}
              color={rateColor(data?.hard_bounce_rate, 0.3, 0.5, false)}
              caption="Target ≤ 0.3%"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricTile
              label="Open Rate"
              value={fmtPct(data?.open_rate)}
              color={rateColor(data?.open_rate, 25, 15, true)}
              caption="Benchmark 20–35%"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricTile
              label="Click Rate"
              value={fmtPct(data?.click_rate)}
              color={rateColor(data?.click_rate, 3, 1.5, true)}
              caption="Benchmark 2–5%"
            />
          </Grid>
          <Grid item xs={6}>
            <MetricTile
              label="Unsub Rate"
              value={fmtPct(data?.unsub_rate)}
              color={rateColor(data?.unsub_rate, 0.3, 0.5, false)}
              caption="Target ≤ 0.3%"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Loading skeleton for a single panel
// ─────────────────────────────────────────────────────────────────────────
const PanelSkeleton: React.FC<{ accent: string }> = ({ accent }) => (
  <Card sx={{ borderRadius: 2, borderTop: `6px solid ${accent}`, height: "100%" }}>
    <CardContent>
      <Skeleton variant="text" width="55%" height={36} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1.5 }} />
      <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 1.5, mb: 1.5 }} />
      <Grid container spacing={1.5}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    </CardContent>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────
const DeliverabilityHealthPage: React.FC = () => {
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" });
  const [data, setData] = useState<DeliverabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res: any = await apiService.get<DeliverabilityResponse>(
          `/api/admin/email-analytics/deliverability?${emailAnalyticsQuery(period)}`,
        );
        if (!mounted) return;
        // Responses look like { success, data: { transactional, marketing } }
        const payload: DeliverabilityResponse =
          res?.data?.transactional || res?.data?.marketing
            ? res.data
            : res?.transactional || res?.marketing
              ? res
              : (res?.data ?? res ?? {});
        setData(payload);
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load deliverability health");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [period]);

  return (
    <PillarPageShell
      pillar="measure"
      title="Deliverability Health"
      subtitle={`Read-only snapshot over ${describeRange(period)}. The Transactional and Marketing pillars are kept hard-separated per audit Principle 3.11.8 — different sender tracks, KPIs, and thresholds. They are never blended.`}
      helpTipId="comm.overview.title"
      rightActions={<TimeRangeFilter value={period} onChange={setPeriod} />}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <PanelSkeleton accent="#5D6D7E" />
          </Grid>
          <Grid item xs={12} md={6}>
            <PanelSkeleton accent="#D4AC0D" />
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <PillarPanel
              accent="#5D6D7E"
              badge="Transactional"
              badgeIcon="🔒"
              title="Transactional"
              subtitle="Must-deliver operational mail · sender track A"
              data={data?.transactional}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PillarPanel
              accent="#D4AC0D"
              badge="Marketing"
              badgeIcon="🎯"
              title="Marketing"
              subtitle="Discretionary opt-in outreach · sender track B"
              data={data?.marketing}
            />
          </Grid>
        </Grid>
      )}
    </PillarPageShell>
  );
};

export default DeliverabilityHealthPage;
